import { Button, Checkbox, Input, Modal, Space, Typography } from 'antd';
import React, { FC, useState, useEffect } from 'react';
import { KeypointCollectionLabel } from 'models/keypoints';
import {
  ScrollableList,
  SelectButtonsRow,
  InfoTextRow,
  CheckboxRow,
  CommentBox,
} from './RejectModal.styled';

const { Text } = Typography;

interface IProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  handleOk: (rejectedKeypoints: string[], comment: string) => void;
  segmentations: KeypointCollectionLabel[]; // Renamed from segmentations to keypoints
  initialRejectedIds?: string[];
}

const RejectModal: FC<IProps> = props => {
  const {
    isModalOpen,
    handleCancel,
    handleOk,
    segmentations: keypoints,
    initialRejectedIds = [],
  } = props;
  const [comment, setComment] = useState('');
  const [rejectedKeypoints, setRejectedKeypoints] = useState<Set<string>>(
    new Set(initialRejectedIds)
  );

  useEffect(() => {
    if (isModalOpen) {
      // default to selecting all keypoints for faster technician reviews
      const allIds = keypoints.map(kp => kp.icid);
      setRejectedKeypoints(new Set(allIds));
      setComment('');
    }
  }, [isModalOpen, initialRejectedIds]);

  const handleCheckboxChange = (keypointId: string, checked: boolean) => {
    setRejectedKeypoints(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(keypointId);
      } else {
        newSet.delete(keypointId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = keypoints.map(kp => kp.icid);
    setRejectedKeypoints(new Set(allIds));
  };

  const handleSelectNone = () => {
    setRejectedKeypoints(new Set());
  };

  return (
    <Modal
      title="Select Keypoints to Reject"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <SelectButtonsRow>
            <Button size="small" onClick={handleSelectAll} style={{ marginRight: 8 }}>
              Select All
            </Button>
            <Button size="small" onClick={handleSelectNone}>
              Select None
            </Button>
          </SelectButtonsRow>
          <InfoTextRow>
            <Text type="secondary">
              Only selected keypoints will be rejected; others will remain unchanged.
            </Text>
          </InfoTextRow>
        </div>

        <ScrollableList>
          {keypoints.map(keypoint => {
            const isRejected = rejectedKeypoints.has(keypoint.icid);
            return (
              <CheckboxRow key={keypoint.icid}>
                <Checkbox
                  checked={isRejected}
                  onChange={e => handleCheckboxChange(keypoint.icid, e.target.checked)}
                >
                  <Space direction="vertical" size={0}>
                    <Text strong>{keypoint.keypointCollectionClassValue}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {keypoint.icid}
                      {isRejected && ' (Currently Rejected)'}
                    </Text>
                  </Space>
                </Checkbox>
              </CheckboxRow>
            );
          })}
        </ScrollableList>

        <CommentBox>
          <Text strong>Comment (optional):</Text>
          <Input.TextArea
            showCount
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={255}
            placeholder="Add a comment for the rejected keypoints..."
            style={{ marginTop: 8, resize: 'none' }}
          />
        </CommentBox>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} size={12}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => handleOk(Array.from(rejectedKeypoints), comment)}
            disabled={rejectedKeypoints.size === 0}
          >
            Reject ({rejectedKeypoints.size} selected)
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};
export default RejectModal;
